import { User, Briefcase } from "lucide-react";
import { useAudience, type Audience } from "@/hooks/useAudience";
import { motion } from "framer-motion";

interface AudienceToggleProps {
  variant?: "light" | "dark";
  className?: string;
}

const options: { value: Audience; label: string; icon: typeof User }[] = [
  { value: "owner", label: "Sou proprietário", icon: User },
  { value: "broker", label: "Sou corretor / imobiliária", icon: Briefcase },
];

export const AudienceToggle = ({ variant = "dark", className = "" }: AudienceToggleProps) => {
  const { audience, setAudience } = useAudience();

  const isDark = variant === "dark";
  const containerCls = isDark
    ? "bg-white/10 border-white/15 backdrop-blur-md"
    : "bg-muted border-border";
  const inactiveCls = isDark ? "text-white/70 hover:text-white" : "text-muted-foreground hover:text-foreground";

  return (
    <div
      role="tablist"
      aria-label="Selecione seu perfil"
      className={`inline-flex p-1 rounded-full border ${containerCls} ${className}`}
    >
      {options.map((opt) => {
        const active = audience === opt.value;
        const Icon = opt.icon;
        return (
          <button
            key={opt.value}
            role="tab"
            aria-selected={active}
            onClick={() => setAudience(opt.value)}
            className={`relative inline-flex items-center gap-2 px-4 py-2 text-sm font-display font-medium rounded-full transition-colors ${
              active ? "text-foreground" : inactiveCls
            }`}
          >
            {active && (
              <motion.span
                layoutId="audience-toggle-pill"
                className="absolute inset-0 rounded-full bg-accent shadow-card"
                transition={{ type: "spring", stiffness: 400, damping: 32 }}
              />
            )}
            <Icon size={16} className="relative z-10" />
            <span className="relative z-10 whitespace-nowrap">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
};

export default AudienceToggle;